import { utils } from '@weapp/ui';
import { toJS } from 'mobx';
import {
  CardStyle, ComLayoutScope, Comp, CompactType, LayoutItem, PageStyle,
} from '../types';

export const calcHeight = (
  /** 卡片高度 */
  h: number,
  /** 卡片的左右间距 */
  cardTB: number,
  /** 行高，对应设计器拖拽的步长 */
  rowHeight: number,
) => Math.round(rowHeight * h + Math.max(0, h - 1) * cardTB);

// 计算每一列的宽度
export const calcColWidth = (
  /** 容器宽度 */
  containerWidth: number,
  /** 卡片左右间距 */
  cardLR: number = 10,
  /** 页面总划分为多少列 */
  column: number,
) => (containerWidth - cardLR * (column - 1)) / column;

// 计算每个卡片的实际宽度
export const cardWidth = (
  // 卡片所占的列数
  w: number,
  // 每一列的宽度
  colWidth: number,
  // 卡片的左右间距
  cardLR: number,
) => w * (colWidth + cardLR) - cardLR;

/**
 * 页面padding
 */
export function getContainerPadding(pageStyle: PageStyle | CardStyle) {
  const { padding } = pageStyle;

  if (!padding) {
    return [0, 0, 0, 0];
  }

  const spaces = padding!.space.split(' ');

  if (spaces.length === 1) {
    const _padding = parseInt(spaces[0], 10);

    return [_padding, _padding, _padding, _padding];
  }

  return spaces.map((_space) => parseInt(_space, 10));
}

/**
 * tab内容区域的页面padding
 */
export function getTabContentPadding(pageStyle: PageStyle | CardStyle) {
  const { padding } = pageStyle;

  if (!padding || padding.selectedId === 'default') {
    return [0, 0, 0, 0];
  }

  const spaces = padding!.space.split(' ');

  if (spaces.length === 1) {
    const _padding = parseInt(spaces[0], 10);

    return [_padding, _padding, _padding, _padding];
  }

  return spaces.map((_space) => parseInt(_space, 10));
}

/**
 * 获取Tab组件的样式配置
 */
export function getTabStyles(comp: Comp) {
  const dftTabStyles = {
    padding: {
      selectedId: 'default',
      space: '',
    },
    rowSpace: 10,
    colSpace: 10,
  };

  const _comp = toJS(comp);

  if (utils.isEmpty(_comp)) return dftTabStyles;

  const styles = _comp.config.styles || [];

  if (styles && Array.isArray(styles) && styles.length && _comp.type === 'Tab') {
    let contentStyle = undefined as any;
    const tabStyle = styles.find((style) => style?.useCustom && style?.category === '5');

    const { useCustom, customStyle, libStyle } = tabStyle || styles[0];

    if (useCustom) {
      contentStyle = customStyle?.content || libStyle?.content;
    } else {
      contentStyle = libStyle?.content;
    }

    if (!utils.isEmpty(contentStyle)) {
      return {
        ...dftTabStyles,
        ...contentStyle,
      };
    }
    return dftTabStyles;
  }

  return dftTabStyles;
}

function getColRowSpace(space?: number | string): number {
  // 没有行间距和列间距字段时，取默认值10
  if (typeof space === 'undefined') {
    return 10;
  }

  // 如果返回值为空字符串，取0
  if (space === '') {
    return 0;
  }

  return Number(space);
}

export function getParentComAttr(type: string, pCom: Comp) {
  const { styles } = toJS(pCom).config;

  if (styles && Array.isArray(styles) && styles.length) {
    let content = undefined as any;
    const tabStyle = styles.find((style) => style?.useCustom && style?.category === '5');

    const { useCustom, customStyle, libStyle } = tabStyle || styles[0];

    if (useCustom) {
      content = customStyle?.content || libStyle?.content;
    } else {
      content = libStyle?.content;
    }

    if (type === 'margin') {
      // eslint-disable-next-line max-len
      return content
        ? [getColRowSpace(content?.colSpace), getColRowSpace(content?.rowSpace)]
        : [10, 10];
    }

    if (type === 'padding') {
      return content ? getTabContentPadding(content as any) : [0, 0, 0, 0];
    }
  }
}

/**
 * 是否为浮动组件 位置固定
 */
export function isFloatCom(com: Comp) {
  return com.config.float === true;
}

/**
 * 判断是否为layoutScope下的组件
 */
export const filterComByScope = (com: Comp, layoutScope?: ComLayoutScope): boolean => {
  const { scope } = com.config!.layout;
  const isContentLayout = !layoutScope && !scope;

  return isContentLayout || scope === layoutScope;
};

/**
 * 滤掉页面解锁状态下添加的自定义组件
 */
export const filterComByPageLock = (com: any, lock?: boolean) => {
  if (lock) {
    return !com?.customConfig || Object.keys(com.customConfig).length !== 0;
  }

  return true;
};

/**
 * 判断是否是老eb的表格页面和表单页面组件
 */
export const isFormmodeComps = (com: Comp) => {
  const formmodeComps = ['TableList', 'TableSearch', 'TableHeader', 'FormLayout'];

  return formmodeComps.indexOf(com.type) > -1;
};

/**
 * 判断组件是否为容器内的子组件
 */
export const isFlowCom = (com: Comp) => com.config?.flow?.parent;

const containers = [
  'GridContainer',
  'GridMultiColumn',
  'Container',
  'MultiColumn',
  'InternalContainer',
];
/**
 * 判断组件是否为容器和多列容器，不包含类容器（例如抽屉、选项卡等）
 */
export const isExactContainer = (com: Comp) => containers.indexOf(com.type) > -1;

/**
 * 判断容器里面是否有子组件
 */
export const isEmptyContainer = (com: Comp) => com.config.layout?.isEmpty;

export const isAutoHeightCom = (com: Comp) => {
  const { config } = com;

  return config.flow?.isCanvas && config.layout?.autoHeight && !config.flow?.parent;
};

// 组件是否可见，未设置hidden的外层组件，不是tab选项卡内和流式容器内的子组件
// eslint-disable-next-line max-len
const isVisible = (com: Comp) => !com.pid && !isFlowCom(com) && !isEmptyContainer(com);

export function getComs(coms: Comp[], _layoutScope?: ComLayoutScope): Comp[] {
  return coms.filter((com) => !isFloatCom(com) && filterComByScope(com, _layoutScope));
}

export function getVisibleComs(coms: Comp[]) {
  return coms.filter(isVisible);
}

/** layout.hidden逻辑调整，但是预览时页面还是得显示暂无内容 */
export const getLayoutShowComs = (coms: Comp[]) => coms.filter((com) => !com.config.layout.hidden);

/**
 * Get all static elements.
 * @param  {Array} layout Array of layout objects.
 * @return {Array}        Array of static layout items..
 */
function getStatics(layouts: LayoutItem[]): LayoutItem[] {
  return layouts.filter((l) => l.static);
}

/**
 * Given two layoutitems, check if they collide.
 */
export function collides(l1: LayoutItem, l2: LayoutItem): boolean {
  if (l1.i === l2.i) return false; // same element
  if (l1.x + l1.w <= l2.x) return false; // l1 is left of l2
  if (l1.x >= l2.x + l2.w) return false; // l1 is right of l2
  if (l1.y + l1.h <= l2.y) return false; // l1 is above l2
  if (l1.y >= l2.y + l2.h) return false; // l1 is below l2
  return true; // boxes overlap
}

/**
 * Returns the first item this layout collides with.
 * It doesn't appear to matter which order we approach this from, although
 * perhaps that is the wrong thing to do.
 *
 * @param  {Object} layoutItem Layout item.
 * @return {Object|undefined}  A colliding layout item, or undefined.
 */
export function getFirstCollision(layouts: LayoutItem[], layoutItem: LayoutItem): LayoutItem {
  for (let i = 0, len = layouts.length; i < len; i++) {
    if (collides(layouts[i], layoutItem)) {
      return layouts[i];
    }
  }
  return undefined as any;
}

const heightWidth = { x: 'w', y: 'h' };
/* eslint-disable max-len */
/**
 * Before moving item down, it will check if the movement will cause collisions and move those items down before.
 */
function resolveCompactionCollision(
  layouts: LayoutItem[],
  item: LayoutItem,
  moveToCoord: number,
  axis: 'x' | 'y',
) {
  const sizeProp = heightWidth[axis];
  item[axis] += 1;
  const itemIndex = layouts.map((layoutItem) => layoutItem.i).indexOf(item.i);

  // Go through each item we collide with.
  for (let i = itemIndex + 1; i < layouts.length; i++) {
    const otherItem = layouts[i];
    // Ignore static items
    if (otherItem.static) continue;

    // Optimization: we can break early if we know we're past this el
    // We can do this b/c it's a sorted layout
    if (otherItem.y > item.y + item.h) break;

    if (collides(item, otherItem)) {
      resolveCompactionCollision(layouts, otherItem, moveToCoord + item[sizeProp], axis);
    }
  }

  item[axis] = moveToCoord;
}

/**
 * Return the bottom coordinate of the layout.
 *
 * @param  {Array} layout Layout array.
 * @return {Number}       Bottom coordinate.
 */
export function bottom(layouts: LayoutItem[]): number {
  let max = 0;
  let bottomY: number;

  layouts.forEach((l) => {
    bottomY = l.y + l.h;

    if (bottomY > max) {
      max = bottomY;
    }
  });

  return max;
}

/**
 * Compact an item in the layout.
 *
 * Modifies item.
 *
 */
function compactItem(
  compareWith: LayoutItem[],
  l: LayoutItem,
  compactType: CompactType,
  cols: number,
  fullLayout: LayoutItem[],
): LayoutItem {
  const compactV = compactType === 'vertical';
  const compactH = compactType === 'horizontal';
  if (compactV) {
    // Bottom 'y' possible is the bottom of the layout.
    // This allows you to do nice stuff like specify {y: Infinity}
    // This is here because the layout must be sorted in order to get the correct bottom `y`.
    l.y = Math.min(bottom(compareWith), l.y);
    // Move the element up as far as it can go without colliding.
    while (l.y > 0 && !getFirstCollision(compareWith, l)) {
      l.y--;
    }
  } else if (compactH) {
    // Move the element left as far as it can go without colliding.
    while (l.x > 0 && !getFirstCollision(compareWith, l)) {
      l.x--;
    }
  }

  // Move it down, and keep moving it down if it's colliding.
  let collidesVal;
  /* eslint-disable no-cond-assign */
  while ((collidesVal = getFirstCollision(compareWith, l))) {
    if (compactH) {
      resolveCompactionCollision(fullLayout, l, collidesVal.x + collidesVal.w, 'x');
    } else {
      resolveCompactionCollision(fullLayout, l, collidesVal.y + collidesVal.h, 'y');
    }
    // Since we can't grow without bounds horizontally, if we've overflown, let's move it down and try again.
    if (compactH && l.x + l.w > cols) {
      l.x = cols - l.w;
      l.y++;
    }
  }

  // Ensure that there are no negative positions
  l.y = Math.max(l.y, 0);
  l.x = Math.max(l.x, 0);

  return l;
}

/**
 * Given a layout, make sure all elements fit within its bounds.
 *
 * Modifies layout items.
 *
 * @param  {Array} layout Layout array.
 * @param  {Number} bounds Number of columns.
 */
function correctBounds(layout: LayoutItem[], bounds: { cols: number }): LayoutItem[] {
  const collidesWith = getStatics(layout);
  for (let i = 0, len = layout.length; i < len; i++) {
    const l = layout[i];
    // Overflows right
    if (l.x + l.w > bounds.cols) l.x = bounds.cols - l.w;
    // Overflows left
    if (l.x < 0) {
      l.x = 0;
      l.w = bounds.cols;
    }
    if (!l.static) collidesWith.push(l);
    else {
      // If this is static and collides with other statics, we must move it down.
      // We have to do something nicer than just letting them overlap.
      while (getFirstCollision(collidesWith, l)) {
        l.y++;
      }
    }
  }
  return layout;
}

/**
 * Sort layout items by column ascending then row ascending.
 *
 * Does not modify Layout.
 */
function sortLayoutItemsByColRow(layout: LayoutItem[]): LayoutItem[] {
  return layout.slice(0).sort((a, b) => {
    if (a.x > b.x || (a.x === b.x && a.y > b.y)) {
      return 1;
    }
    return -1;
  });
}

/**
 * Sort layout items by row ascending and column ascending.
 *
 * Does not modify Layout.
 */
function sortLayoutItemsByRowCol(layout: LayoutItem[]): LayoutItem[] {
  // Slice to clone array as sort modifies
  return layout.slice(0).sort((a, b) => {
    if (a.y > b.y || (a.y === b.y && a.x > b.x)) {
      return 1;
    }
    if (a.y === b.y && a.x === b.x) {
      // Without this, we can get different sort results in IE vs. Chrome/FF
      return 0;
    }
    return -1;
  });
}

/**
 * Get layout items sorted from top left to right and down.
 *
 * @return {Array} Array of layout objects.
 * @return {Array}        Layout, sorted static items first.
 */
function sortLayoutItems(layout: LayoutItem[], compactType: CompactType): LayoutItem[] {
  if (compactType === 'horizontal') {
    return sortLayoutItemsByColRow(layout);
  }
  return sortLayoutItemsByRowCol(layout);
}

/**
 * Given a layout, compact it. This involves going down each y coordinate and removing gaps
 * between items.
 *
 * Does not modify layout items (clones). Creates a new layout array.
 *
 * @param  {Array} LayoutItem[] LayoutItem[].
 * @param  {Boolean} verticalCompact Whether or not to compact the layout
 *   vertically.
 * @return {Array}       Compacted Layout.
 */
export function compact(
  layouts: LayoutItem[],
  compactType: CompactType,
  cols: number,
): LayoutItem[] {
  // Statics go in the compareWith array right away so items flow around them.
  const compareWith = getStatics(layouts);
  // We go through the items by row and column.
  const sorted = sortLayoutItems(layouts, compactType);
  // Holding for new items.
  const out = Array(layouts.length);

  for (let i = 0, len = sorted.length; i < len; i++) {
    let l = { ...sorted[i] };

    // Don't move static elements
    if (!l.static) {
      l = compactItem(compareWith, l, compactType, cols, sorted);

      // Add to comparison array. We only collide with items before this one.
      // Statics are already in this array.
      compareWith.push(l);
    }

    // Add to output array to make sure they still come out in the right order.
    out[layouts.indexOf(sorted[i])] = l;

    // Clear moved flag, if it exists.
    l.moved = false;
  }

  return out;
}

export function reLayout(layouts: LayoutItem[], compactType: CompactType, cols: number) {
  const newLayouts = correctBounds(layouts, { cols });

  return compact(newLayouts, compactType, cols);
}

export const compatibleGetShrinkWay = (defaultShrink?: string | boolean) => {
  if (typeof defaultShrink === 'boolean') {
    return defaultShrink === true ? '1' : '0';
  }
  if (defaultShrink === undefined) {
    return '0';
  }
  return defaultShrink;
};

export const dealShrinkOrShow = (
  defaultShrink?: string | boolean,
  shrinkWidth?: string,
  width?: number,
) => {
  const compatibleShrinkWay = compatibleGetShrinkWay(defaultShrink);
  // 0是展开，1是收缩，2是根据屏幕大小收缩
  return (
    compatibleShrinkWay === '0'
    || (compatibleShrinkWay === '2'
      && (shrinkWidth === ''
        || (width !== undefined && shrinkWidth !== undefined && Number(shrinkWidth) < width)))
  );
};

export const getParentId = (com?: Comp) => com?.pid || com?.config?.flow?.parent;

export const getRootCom = (com: Comp, coms: Comp[]) => {
  let pid = getParentId(com);
  let pCom = com;

  while (pid) {
    // eslint-disable-next-line no-loop-func
    pCom = coms.find(({ id }) => id === pid)!;
    pid = getParentId(pCom);
  }

  return pCom;
};

// 判断是对象，并且对象中有值
export const checkObject = (data: any) => Object.prototype.toString.call(data) === '[object Object]' && Object.keys(data).length > 0;

// 为了兼容老数据的方法
export const initLinkedNodes = (com: any) => {
  const linkedNodes: any = {};
  if (com.type === 'GridMultiColumn') {
    if (com.config.flow?.nodes?.length && !com.config?.flow?.linkedNodes) {
      com.config.flow?.nodes.forEach((id: string, index: number) => {
        linkedNodes[`${index}`] = id;
      });
    }
  }
  if (com.type === 'NewTab') {
    if (com.config?.tabs?.length && !com.config?.flow?.linkedNodes) {
      com.config.tabs.forEach((tab: any) => {
        linkedNodes[tab.id] = tab?.droppableId || '';
      });
    }
  }
  if (com.type === 'EbTableLayout') {
    if (com.config.flow?.nodes?.length && !com.config?.flow?.linkedNodes) {
      Object.keys(com.config.cellMergeInfo || {}).forEach((key) => {
        linkedNodes[key] = com.config.cellMergeInfo[key].droppableId || '';
      });
    }
  }

  if (checkObject(linkedNodes)) {
    com.config.flow.linkedNodes = {
      ...linkedNodes,
    };
  }
  return com;
};

export const pickComLayoutH = (coms: any = []) => coms
  .filter((com: any) => {
    if (com.config?.flow?.isCanvas && com.config.layout.autoHeight && !com.config.flow.parent) {
      return com;
    }
    return false;
  })
  .map((_c: any) => _c.config.layout.h);

export default {};
